/**
 * useAutomationBadge
 *
 * Per-automation-type badge metadata and live countdown helpers.
 * Used by the shared AutomationBadges component.
 */

import { ref, onUnmounted } from 'vue';
import { tt } from '../utils/consoleText';
import type { Bilingual } from '../config/bilingual';

// ─────────────────────────────────────────────────────────────────────────────
// Badge metadata per automation type
// ─────────────────────────────────────────────────────────────────────────────

export interface AutomationBadgeMeta {
  /** Material Symbols icon name */
  icon: string;
  /** Category colour class (Tailwind bg class for active state) */
  activeBg: string;
  /** Category colour class for idle (enabled but not running) */
  idleBg: string;
  /**
   * DeviceValue key (relative, without type-prefix) that signals the
   * automation is currently "active" / "running".  e.g. "timer_active".
   * null = always show as idle if enabled.
   */
  activeStateKey: string | null;
  /**
   * DeviceValue key (relative) whose value is an ISO timestamp marking the
   * future moment when the automation will next fire the opposite action.
   * Used to drive a live countdown.
   */
  countdownIsoKey: string | null;
  /**
   * DeviceValue key (relative) whose value is a numeric seconds-remaining
   * figure (alternative to an ISO timestamp, e.g. light_motion_switch).
   */
  countdownSecondsKey: string | null;
  /**
   * Human-readable label (the reader's language, picked by `tt()`), shown in
   * the tooltip and in the automations list.
   */
  label: Bilingual;
  /**
   * DeviceValue key (relative) holding an "active mode" string for
   * multi-mode automations (e.g. shade_sun_tracking → "active_source").
   */
  activeModeKey?: string | null;
  /** Optional label map for active-mode values (tooltips/badge text). */
  modeLabels?: Record<string, Bilingual>;
}

/** Human labels for the sun controller position sources. */
const SUN_CONTROLLER_SOURCE_LABELS: Record<string, Bilingual> = {
  sun_tracking: { en: 'Sun tracking', sl: 'Sončno vodenje' },
  brightness: { en: 'Brightness', sl: 'Svetlost' },
  schedule: { en: 'Schedule', sl: 'Urnik' },
  disabled: { en: 'Disabled', sl: 'Onemogočeno' },
};

const BADGE_META: Record<string, AutomationBadgeMeta> = {
  switch_off_timer: {
    icon: 'timer',
    activeBg: 'bg-emerald-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'timer_active',
    countdownIsoKey: 'scheduled_off_at',
    countdownSecondsKey: null,
    label: { en: 'Switch-Off Timer', sl: 'Časovnik izklopa' },
  },
  time_switch: {
    icon: 'schedule',
    activeBg: 'bg-sky-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'schedule_active',
    countdownIsoKey: 'next_switch_at',
    countdownSecondsKey: null,
    label: { en: 'Time Switch', sl: 'Časovno stikalo' },
  },
  light_motion_switch: {
    icon: 'motion_sensor_active',
    activeBg: 'bg-amber-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'timer_active',
    countdownIsoKey: null,
    countdownSecondsKey: 'time_remaining_seconds',
    label: { en: 'Motion Switch', sl: 'Gibalno stikalo' },
  },
  light_brightness_switch: {
    icon: 'light_mode',
    activeBg: 'bg-yellow-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: null,
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Brightness Switch', sl: 'Stikalo po svetlosti' },
  },
  light_external_switch: {
    icon: 'toggle_on',
    activeBg: 'bg-yellow-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: null,
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'External Switch', sl: 'Zunanje stikalo' },
  },
  analog_threshold_alarm: {
    icon: 'notifications_active',
    activeBg: 'bg-red-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: null,
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Threshold Alarm', sl: 'Alarm ob meji' },
  },
  binary_sensor_alarm: {
    icon: 'sensors',
    activeBg: 'bg-red-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'alarm',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Sensor Alarm', sl: 'Alarm senzorja' },
  },
  shade_wind_protection: {
    icon: 'air',
    activeBg: 'bg-blue-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'protection_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Wind Protection', sl: 'Zaščita pred vetrom' },
  },
  shade_rain_protection: {
    icon: 'water_drop',
    activeBg: 'bg-blue-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'protection_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Rain Protection', sl: 'Zaščita pred dežjem' },
  },
  shade_sun_tracking: {
    icon: 'wb_sunny',
    activeBg: 'bg-orange-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'sun_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    activeModeKey: 'active_source',
    modeLabels: SUN_CONTROLLER_SOURCE_LABELS,
    label: { en: 'Sun Controller', sl: 'Sončno vodenje' },
  },
  fan_temperature_controller: {
    icon: 'thermostat',
    activeBg: 'bg-sky-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'controller_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    activeModeKey: 'control_source',
    modeLabels: {
      threshold: { en: 'Threshold', sl: 'Mejna vrednost' },
      pid: { en: 'PID', sl: 'PID' },
    },
    label: { en: 'Fan Temp Controller', sl: 'Krmilnik temperature ventilatorja' },
  },
  fan_presence_timer: {
    icon: 'mode_fan',
    activeBg: 'bg-sky-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'fan_active',
    countdownIsoKey: null,
    countdownSecondsKey: 'time_remaining_seconds',
    activeModeKey: null,
    modeLabels: undefined,
    label: { en: 'Fan Presence Timer', sl: 'Časovnik prisotnosti ventilatorja' },
  },
  fan_external_control: {
    icon: 'switch',
    activeBg: 'bg-sky-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'fan_on',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    activeModeKey: null,
    modeLabels: undefined,
    label: { en: 'Fan External Control', sl: 'Zunanje krmiljenje ventilatorja' },
  },
  cover_external_control: {
    icon: 'switch',
    activeBg: 'bg-green-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: null,
    countdownIsoKey: null,
    countdownSecondsKey: null,
    activeModeKey: 'last_action',
    modeLabels: {
      open: { en: 'Open', sl: 'Odprto' },
      close: { en: 'Close', sl: 'Zaprto' },
      stop: { en: 'Stop', sl: 'Ustavljeno' },
    },
    label: { en: 'Cover External Control', sl: 'Zunanje krmiljenje senčil' },
  },
  irrigation_controller: {
    icon: 'water',
    activeBg: 'bg-teal-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'running',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Irrigation', sl: 'Namakanje' },
  },
  water_leak_guard: {
    icon: 'water_damage',
    activeBg: 'bg-red-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'leak_alarm',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Water Leak Guard', sl: 'Zaščita pred puščanjem vode' },
  },
  tank_refill_control: {
    icon: 'water_full',
    activeBg: 'bg-cyan-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'refill_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Tank Refill', sl: 'Polnjenje rezervoarja' },
  },
  pump_dry_run_protection: {
    icon: 'water_pump',
    activeBg: 'bg-red-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'dry_run_alarm',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Pump Dry-Run', sl: 'Zaščita črpalke' },
  },
  irrigation_rain_skip: {
    icon: 'water_drop',
    activeBg: 'bg-teal-500',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'skip_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Irrigation Rain Skip', sl: 'Preskok namakanja ob dežju' },
  },
  lock_webhook_trigger: {
    icon: 'webhook',
    activeBg: 'bg-violet-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: null,
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Webhook Trigger', sl: 'Sprožilec webhook' },
  },
  appliance_run_limit: {
    icon: 'timer',
    activeBg: 'bg-amber-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'limit_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Appliance Run Limit', sl: 'Omejitev delovanja aparata' },
  },
  appliance_cycle_finished: {
    icon: 'check_circle',
    activeBg: 'bg-emerald-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'running',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Appliance Cycle Finished', sl: 'Konec cikla aparata' },
  },
  robot_job_schedule: {
    icon: 'event_repeat',
    activeBg: 'bg-sky-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'job_running',
    countdownIsoKey: 'next_run_at',
    countdownSecondsKey: null,
    label: { en: 'Robot Job Schedule', sl: 'Urnik robota' },
  },
  robot_rain_return: {
    icon: 'rainy',
    activeBg: 'bg-blue-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'rain_hold',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Robot Rain Return', sl: 'Vrnitev robota ob dežju' },
  },
  network_device_offline: {
    icon: 'wifi_off',
    activeBg: 'bg-red-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'offline_alarm',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Network Device Offline', sl: 'Naprava brez povezave' },
  },
  pool_filtration_schedule: {
    icon: 'water_pump',
    activeBg: 'bg-cyan-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'filtration_active',
    countdownIsoKey: 'next_run_at',
    countdownSecondsKey: null,
    label: { en: 'Pool Filtration', sl: 'Filtracija bazena' },
  },
  pool_chemistry_guard: {
    icon: 'science',
    activeBg: 'bg-emerald-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'dosing_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Pool Chemistry', sl: 'Kakovost vode bazena' },
  },
  spa_heating_ready_by: {
    icon: 'device_thermostat',
    activeBg: 'bg-orange-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'heating_active',
    countdownIsoKey: 'ready_at',
    countdownSecondsKey: null,
    label: { en: 'Spa Heating', sl: 'Ogrevanje bazena' },
  },
  sauna_session_schedule: {
    icon: 'sauna',
    activeBg: 'bg-orange-700',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'session_active',
    countdownIsoKey: 'next_session_at',
    countdownSecondsKey: null,
    label: { en: 'Sauna Schedule', sl: 'Urnik savne' },
  },
  gym_equipment_session: {
    icon: 'fitness_center',
    activeBg: 'bg-purple-600',
    idleBg: 'bg-gray-700/70',
    activeStateKey: 'session_active',
    countdownIsoKey: null,
    countdownSecondsKey: null,
    label: { en: 'Gym Session', sl: 'Trening' },
  },
};

const FALLBACK_META: AutomationBadgeMeta = {
  icon: 'auto_mode',
  activeBg: 'bg-gray-600',
  idleBg: 'bg-gray-700/70',
  activeStateKey: null,
  countdownIsoKey: null,
  countdownSecondsKey: null,
  label: { en: 'Automation', sl: 'Avtomatizacija' },
};

export function getBadgeMeta(automationType: string): AutomationBadgeMeta {
  return BADGE_META[automationType] ?? FALLBACK_META;
}

/**
 * The automation type's name in the reader's language, or the raw key dressed up
 * when the type is unknown (the same fallback the automations list used before).
 */
export function formatAutomationTypeLabel(automationType: string): string {
  const meta = BADGE_META[automationType];
  if (meta) return tt(meta.label.en, meta.label.sl);
  return automationType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Prefixes a state key with the automation type so it matches the
 * DeviceValue key stored on the device (e.g. "switch_off_timer__timer_active").
 */
export function prefixedKey(automationType: string, stateKey: string): string {
  return `${automationType}__${stateKey}`;
}

/**
 * Resolve a human-readable active-mode label for a multi-mode automation
 * (e.g. "Heat protection"), or null when no meaningful mode is active.
 */
export function getActiveModeLabel(automationType: string, currentValues: Record<string, unknown>): string | null {
  const meta = BADGE_META[automationType];
  if (!meta?.activeModeKey) return null;
  const value = currentValues[prefixedKey(automationType, meta.activeModeKey)];
  if (value == null || value === '' || value === 'none' || value === 'inhibited') {
    return null;
  }
  if (typeof value === 'string') {
    const label = meta.modeLabels?.[value];
    return label ? tt(label.en, label.sl) : value.replace(/_/g, ' ');
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Countdown helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Format a remaining-milliseconds figure as "M:SS" or "H:MM:SS". */
export function formatCountdown(remainingMs: number): string {
  if (remainingMs <= 0) return '0:00';
  const totalSec = Math.ceil(remainingMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * Reactive countdown composable.
 * Ticks every 1 s and returns a string like "4:32" or "1:02:15".
 * Automatically clears the interval when the component unmounts or when
 * `targetIso` becomes null/falsy.
 *
 * @param getTargetIso  Getter that returns the ISO target string (or null)
 */
export function useIsoCountdown(getTargetIso: () => string | null | undefined) {
  const display = ref<string>('');

  function tick() {
    const iso = getTargetIso();
    if (!iso) {
      display.value = '';
      return;
    }
    const remaining = new Date(iso).getTime() - Date.now();
    display.value = remaining > 0 ? formatCountdown(remaining) : '0:00';
  }

  tick();
  const interval = setInterval(tick, 1000);
  onUnmounted(() => clearInterval(interval));

  return display;
}

/**
 * Reactive countdown composable for a seconds-remaining numeric value.
 * The value is expected to decrease each second (pushed via socket).
 * We tick every 1 s purely as a visual fallback between socket updates.
 *
 * @param getSeconds  Getter that returns the current seconds remaining (number | null)
 */
export function useSecondsCountdown(getSeconds: () => number | null | undefined) {
  const display = ref<string>('');

  // Mirror last-received value and count down locally between socket updates
  let localMs = 0;

  function sync() {
    const s = getSeconds();
    if (s == null || s <= 0) {
      display.value = '';
      localMs = 0;
      return;
    }
    localMs = s * 1000;
    display.value = formatCountdown(localMs);
  }

  sync();

  const interval = setInterval(() => {
    const s = getSeconds();
    if (s == null) {
      display.value = '';
      return;
    }
    // Re-sync from prop if it changed meaningfully (socket update arrived)
    const fresh = s * 1000;
    if (Math.abs(fresh - localMs) > 1500) localMs = fresh;
    else localMs = Math.max(0, localMs - 1000);
    display.value = localMs > 0 ? formatCountdown(localMs) : '';
  }, 1000);

  onUnmounted(() => clearInterval(interval));

  return display;
}

/**
 * Reactive numeric countdown composable for a seconds-remaining value.
 * Same local ticking + drift-resync logic as useSecondsCountdown, but
 * returns the live number of seconds (for progress rings/ratios) instead of
 * a formatted string.
 *
 * @param getSeconds  Getter that returns the current seconds remaining (number | null)
 */
export function useSecondsRemaining(getSeconds: () => number | null | undefined) {
  const remaining = ref(0);

  // Count down locally between socket updates
  let localMs = 0;

  function sync() {
    const s = getSeconds();
    if (s == null || s <= 0) {
      remaining.value = 0;
      localMs = 0;
      return;
    }
    localMs = s * 1000;
    remaining.value = s;
  }

  sync();

  const interval = setInterval(() => {
    const s = getSeconds();
    if (s == null) {
      remaining.value = 0;
      return;
    }
    // Re-sync from the latest value if it changed meaningfully (socket update)
    const fresh = s * 1000;
    if (Math.abs(fresh - localMs) > 1500) localMs = fresh;
    else localMs = Math.max(0, localMs - 1000);
    remaining.value = localMs / 1000;
  }, 1000);

  onUnmounted(() => clearInterval(interval));

  return remaining;
}
